import { useState, useEffect, useRef, useCallback } from 'react';

import {
  StringGroupItem,
  useListStringGroupsQuery,
} from 'src/generated/graphql';
import {
  STRING_GROUPS_FETCH_BATCH_SIZE,
  STRING_GROUPS_INITIAL_BATCHES,
  STRING_GROUPS_SEARCH_DEBOUNCE_MS,
} from 'src/features/namespace/constants';
import { useDebouncedValue } from 'src/common/hooks/useDebouncedValue';

export interface UseStringGroupsDataResult {
  /** Items for the current mode (browse or search). */
  displayItems: StringGroupItem[];
  /** Whether a prefix search is currently active. */
  isSearchActive: boolean;
  /** Whether a Load More request is in flight. */
  isLoadingMore: boolean;
  /** Whether more items can be loaded in the current mode. */
  hasMore: boolean;
  /** Current (immediate) search box text. */
  searchText: string;
  /** Updates the search text; triggers a debounced server-side search. */
  onSearchChange: (text: string) => void;
  /** Fetches the next batch of items in the active mode. */
  handleLoadMore: () => void;
  /** True during the initial page load. */
  initialLoading: boolean;
  /** Error from the most recent query, if any. */
  error: Error | undefined;
}

/**
 * Fetches and manages string groups data with two modes:
 * - Browse: auto-chains 5×100 on mount for progressive loading, then Load More.
 * - Search: debounced prefix search fetches 100, Load More appends 100.
 *
 * Uses `useListStringGroupsQuery` (reactive hook) which works correctly with
 * both MockedProvider (mock mode) and ApolloProvider (real mode).
 */
export function useStringGroupsData(
  namespaceId: string | undefined,
): UseStringGroupsDataResult {
  // --- Browse state ---
  const [allItems, setAllItems] = useState<StringGroupItem[]>([]);
  const [browseToken, setBrowseToken] = useState<string | null>(null);
  const [browseInitialized, setBrowseInitialized] = useState(false);

  // --- Auto-chain state ---
  /** How many batches have been fetched so far (1 = initial query result). */
  const [batchesFetched, setBatchesFetched] = useState(0);
  /** Whether the auto-chain is still running. */
  const [isAutoChaining, setIsAutoChaining] = useState(false);
  /** Ref to track abort conditions during async chain iterations. */
  const autoChainAbortRef = useRef(false);
  /** Ref to track the namespaceId the chain was started for. */
  const chainNamespaceRef = useRef<string | undefined>(undefined);
  /** Ref guard to prevent concurrent Load More calls from double-clicks. */
  const isLoadingMoreRef = useRef(false);

  // --- Search state ---
  const [searchResults, setSearchResults] = useState<StringGroupItem[]>([]);
  const [searchToken, setSearchToken] = useState<string | null>(null);

  // --- UI state ---
  const [searchText, setSearchText] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const debouncedPrefix = useDebouncedValue(
    searchText,
    STRING_GROUPS_SEARCH_DEBOUNCE_MS,
  );

  const isSearchActive = debouncedPrefix.trim().length > 0;

  // Track previous debouncedPrefix to detect search term changes
  const prevPrefixRef = useRef(debouncedPrefix);

  // --- Browse query (active when NOT searching) ---
  const {
    data: browseData,
    loading: browseLoading,
    error: browseError,
    fetchMore: browseFetchMore,
  } = useListStringGroupsQuery({
    variables: {
      namespaceId: namespaceId ?? '',
      includeLatestSnapshot: true,
      pageSize: STRING_GROUPS_FETCH_BATCH_SIZE,
    },
    skip: !namespaceId || isSearchActive,
  });

  // --- Search query (active when searching) ---
  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
    fetchMore: searchFetchMore,
  } = useListStringGroupsQuery({
    variables: {
      namespaceId: namespaceId ?? '',
      includeLatestSnapshot: true,
      pageSize: STRING_GROUPS_FETCH_BATCH_SIZE,
      stringGroupIdPrefix: debouncedPrefix || undefined,
    },
    skip: !namespaceId || !isSearchActive,
  });

  // --- Process browse data: seed items and kick off auto-chain ---
  useEffect(() => {
    if (!browseData?.listStringGroups || isSearchActive) return;

    const items = browseData.listStringGroups.stringGroups;
    const nextToken = browseData.listStringGroups.nextToken ?? null;

    if (!browseInitialized) {
      // First load — set items directly and start auto-chain
      setAllItems(items);
      setBrowseToken(nextToken);
      setBrowseInitialized(true);
      setBatchesFetched(1);

      // Start auto-chain if there are more pages to fetch
      if (nextToken && STRING_GROUPS_INITIAL_BATCHES > 1) {
        setIsAutoChaining(true);
        autoChainAbortRef.current = false;
        chainNamespaceRef.current = namespaceId;
      }
    }
  }, [browseData, isSearchActive, browseInitialized, namespaceId]);

  // --- Auto-chain: fetch remaining batches after initial load ---
  useEffect(() => {
    if (!isAutoChaining || !browseInitialized) return;
    if (!browseToken || batchesFetched >= STRING_GROUPS_INITIAL_BATCHES) {
      // Chain complete — either no more data or reached target batch count
      setIsAutoChaining(false);
      return;
    }

    // Abort conditions checked before starting the fetch
    /* istanbul ignore if -- defensive: abort+isAutoChaining never both true in same batch */
    if (autoChainAbortRef.current) {
      setIsAutoChaining(false);
      return;
    }

    let cancelled = false;

    const fetchNextBatch = async () => {
      try {
        const result = await browseFetchMore({
          variables: {
            nextToken: browseToken,
          },
        });

        // Check abort conditions after async operation
        if (cancelled || autoChainAbortRef.current) {
          return;
        }

        // Verify we're still on the same namespace
        /* istanbul ignore if -- defensive: cleanup cancels async before namespace drift */
        if (chainNamespaceRef.current !== namespaceId) {
          setIsAutoChaining(false);
          return;
        }

        const newItems = result.data.listStringGroups.stringGroups;
        const newToken = result.data.listStringGroups.nextToken ?? null;

        setAllItems((prev) => [...prev, ...newItems]);
        setBrowseToken(newToken);
        setBatchesFetched((prev) => prev + 1);

        // If no more data, stop the chain
        if (!newToken) {
          setIsAutoChaining(false);
        }
      } catch (error) {
        // On error, stop the chain gracefully
        /* istanbul ignore next -- untestable: Apollo's fetchMore resolves with { error } rather than rejecting; a genuine rejection requires a network-level failure that WildcardMockLink cannot simulate */
        console.error('[useStringGroupsData] Auto-chain fetch failed:', error);
        /* istanbul ignore next */
        setIsAutoChaining(false);
      }
    };

    fetchNextBatch();

    return () => {
      cancelled = true;
    };
  }, [
    isAutoChaining,
    browseInitialized,
    browseToken,
    batchesFetched,
    browseFetchMore,
    namespaceId,
  ]);

  // --- Abort auto-chain when search becomes active ---
  useEffect(() => {
    if (isSearchActive && isAutoChaining) {
      autoChainAbortRef.current = true;
      setIsAutoChaining(false);
    }
  }, [isSearchActive, isAutoChaining]);

  // --- Process search data: replace results on new search, accumulate on Load More ---
  useEffect(() => {
    if (!searchData?.listStringGroups || !isSearchActive) return;

    const items = searchData.listStringGroups.stringGroups;
    const nextToken = searchData.listStringGroups.nextToken ?? null;

    // If the prefix changed, this is a fresh search result — replace
    if (prevPrefixRef.current !== debouncedPrefix) {
      setSearchResults(items);
      setSearchToken(nextToken);
      prevPrefixRef.current = debouncedPrefix;
      // Only populate if not yet set (avoids duplicate append on re-render)
    } else if (searchResults.length === 0) {
      /* istanbul ignore next -- defensive: unreachable; prefix change always hits if-branch first */
      setSearchResults(items);
      /* istanbul ignore next -- same unreachable else-if body as above */
      setSearchToken(nextToken);
    }
  }, [searchData, isSearchActive, debouncedPrefix, searchResults.length]);

  // Reset search results when search is cleared
  useEffect(() => {
    if (!isSearchActive) {
      setSearchResults([]);
      setSearchToken(null);
      prevPrefixRef.current = '';
    }
  }, [isSearchActive]);

  // Reset all state when namespace changes
  useEffect(() => {
    autoChainAbortRef.current = true;
    setIsAutoChaining(false);
    setAllItems([]);
    setBrowseToken(null);
    setBrowseInitialized(false);
    setBatchesFetched(0);
    setSearchResults([]);
    setSearchToken(null);
    setSearchText('');
  }, [namespaceId]);

  // --- Load More handler (user-triggered, only active after auto-chain) ---
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || isAutoChaining) return;
    isLoadingMoreRef.current = true;

    setIsLoadingMore(true);

    try {
      if (isSearchActive) {
        if (!searchToken || !searchFetchMore) return;

        const result = await searchFetchMore({
          variables: {
            nextToken: searchToken,
          },
        });

        const newItems = result.data.listStringGroups.stringGroups;
        const newToken = result.data.listStringGroups.nextToken ?? null;

        setSearchResults((prev) => [...prev, ...newItems]);
        setSearchToken(newToken);
      } else {
        if (!browseToken || !browseFetchMore) return;

        const result = await browseFetchMore({
          variables: {
            nextToken: browseToken,
          },
        });

        const newItems = result.data.listStringGroups.stringGroups;
        const newToken = result.data.listStringGroups.nextToken ?? null;

        setAllItems((prev) => [...prev, ...newItems]);
        setBrowseToken(newToken);
      }
    } catch (error) {
      /* istanbul ignore next -- untestable: same as auto-chain; Apollo fetchMore does not reject in mock environments */
      console.error('[useStringGroupsData] Load more failed:', error);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [
    isAutoChaining,
    isSearchActive,
    searchToken,
    browseToken,
    searchFetchMore,
    browseFetchMore,
  ]);

  const displayItems = isSearchActive ? searchResults : allItems;

  // hasMore: in browse mode, only report true AFTER auto-chain is complete and
  // there is still a token. During auto-chain, report false so the button stays
  // disabled (progressive data appears via header count).
  const hasMore = isSearchActive
    ? !!searchToken
    : !isAutoChaining && !!browseToken;

  const initialLoading = !isSearchActive
    ? browseLoading && allItems.length === 0
    : searchLoading && searchResults.length === 0;

  const error = browseError || searchError;

  return {
    displayItems,
    isSearchActive,
    isLoadingMore,
    hasMore,
    searchText,
    onSearchChange: setSearchText,
    handleLoadMore,
    initialLoading,
    error,
  };
}
